import { Field } from "formik";
import React, { useState, useRef } from "react";
import { COLORS } from "../../Constants/theme";

const TextInput = ({
  name,
  value,
  handleChange,
  placeholder,
  isDisabled = false,
  bgColor = "bg-white",
  classes = "",
  type = "text",
  legend = false,
  error = null,
  touched = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const handleFieldsetClick = () => {
    if (!isFocused && !value && inputRef.current) {
      inputRef.current.focus();
      setIsFocused(true);
    }
  };

  const getFieldsetStyles = () => ({
    borderColor:
      error && touched
        ? COLORS.status.error || "#ef4444"
        : isDisabled
          ? "#4b5563" // dark gray-600 for disabled
          : isFocused || value 
            ? "#9ca3af" // indigo-500
            : "#4b5563", // gray-300
    borderWidth: isFocused || value ? "2px" : "1px",
    borderRadius: "0.5rem",
    minHeight: "46px",
    backgroundColor: isDisabled ? "#4b5563" : "#ffffff", // dark gray-600 for disabled, white otherwise
    opacity: isDisabled ? 0.7 : 1,
    cursor: isDisabled ? "not-allowed" : "default",
    boxShadow: "none",
    transition: "all 0.2s ease",
    padding: "0 8px",
    "&:hover": {
      borderColor: isDisabled
        ? "#4b5563" // dark gray-600 for disabled
        : error && touched
          ? COLORS.status.error || "#ef4444"
          : "#9ca3af", // indigo-500
    },
  });

  const getLegendStyles = () => ({
    position: "absolute",
    top: isFocused || value ? "-8px" : "50%",
    transform: isFocused || value ? "none" : "translateY(-50%)",
    left: "12px",
    backgroundColor: isDisabled ? "#f3f4f6" : "#ffffff",
    padding: "0 4px",
    fontSize: isFocused || value ? "0.75rem" : "0.875rem", // 12px when floated, 14px otherwise
    color:
      error && touched
        ? COLORS.status.error || "#ef4444"
        : isFocused
          ? "#1f2937" // indigo-500
          : isDisabled
            ? "#6b7280" // gray-500
            : value
              ? "#1f2937" // gray-800
              : "#6b7280", // gray-400 for placeholder
    transition: "all 0.2s ease",
    zIndex: 10,
  });

  const getInputStyles = () => ({
    width: "100%",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    padding: "8px 12px",
    color: isDisabled ? "#6b7280" : "#1f2937", // gray-500 for disabled, gray-800 otherwise
    fontSize: "0.875rem", // 14px
    cursor: isDisabled ? "not-allowed" : "text",
  });

  return (
    <div className="mb-4 relative w-full">
      {legend ? (
        <fieldset
          className="relative border w-full"
          style={getFieldsetStyles()}
          onClick={handleFieldsetClick}
        >
          <legend style={getLegendStyles()}>
            {placeholder}
            {/* {legend && <span className="text-rose-500 ml-1">*</span>} */}
          </legend>
          <Field
            type={type}
            name={name}
            value={value || ""}
            onChange={handleChange}
            disabled={isDisabled}
            className="w-full"
            style={getInputStyles()}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            innerRef={inputRef}
          />
        </fieldset>
      ) : (
        <Field
          type={type}
          name={name}
          value={value || ""}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled}
          className={`
            w-full 
            px-4 
            py-3 
            rounded-lg 
            ${bgColor}
            border 
            ${
              error && touched
                ? "border-rose-400 focus:ring-rose-400"
                : "border-gray-300 focus:ring-gray-400"
            } 
            text-gray-800 
            placeholder-gray-400 
            transition-all 
            duration-200 
            focus:outline-none 
            focus:ring-2 
            focus:ring-opacity-50 
            focus:border-transparent
            ${isDisabled ? "bg-gray-400 cursor-not-allowed opacity-70" : ""}
            ${classes}
          `}
        />
      )}
      {error && touched && (
        <p className="mt-1 text-sm text-rose-500">{error}</p>
      )}
    </div>
  );
};

export default TextInput;